import { can } from '@pl/domain';
import type { MeDto, WallSnapshotDto } from '@pl/domain';
import { useEffect, useId, useRef, useState } from 'react';
import { LiveWall } from './LiveWall';
import { BootScreen, useStageFit } from './stage';
import { fetchSnapshot, useEvent } from './useWallData';

/** Waits before asking again while the server cannot be reached. */
const RETRY_MS = 5000;

type Got<T> = { ok: true; data: T } | { ok: false; status: number | null };

/** The Wall's own fetch: no office query client here, and no redirect to sign-in on a 401. */
async function get<T>(path: string): Promise<Got<T>> {
  try {
    const res = await fetch(`/api/v1${path}`, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) return { ok: false, status: res.status };
    return { ok: true, data: (await res.json()) as T };
  } catch {
    return { ok: false, status: null };
  }
}

type Board = { kind: 'loading' } | { kind: 'board'; snapshot: WallSnapshotDto; at: number } | { kind: 'refused'; message: string };

/**
 * `/wall` in a browser signed in as a person: the board of a site they pick, as a preview.
 *
 * It is the TV's board, figure for figure. The server masks the snapshot for open counts whoever
 * asks (a worker mid-count is a signed-in person too), so a counted tile is blank here exactly as on
 * the TV, and the bar can say so without checking anything.
 */
export function Preview({ reload, EventSourceImpl, onSignedOut }: { reload: () => void; EventSourceImpl?: typeof EventSource; onSignedOut: () => void }) {
  const frame = useRef<HTMLDivElement>(null);
  const fit = useStageFit(frame);
  const labelId = useId();
  const signedOut = useEvent(onSignedOut);
  const [me, setMe] = useState<MeDto | null>(null);
  const [meAttempt, setMeAttempt] = useState(0);
  const [chosen, setChosen] = useState<string | null>(null);
  const [board, setBoard] = useState<Board>({ kind: 'loading' });
  const [boardAttempt, setBoardAttempt] = useState(0);

  // Who is looking, and which sites they may see.
  useEffect(() => {
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    void (async () => {
      const r = await get<MeDto>('/auth/me');
      if (cancelled) return;
      if (r.ok) setMe(r.data);
      else if (r.status === 401) signedOut();
      else retry = setTimeout(() => setMeAttempt((n) => n + 1), RETRY_MS);
    })();
    return () => {
      cancelled = true;
      clearTimeout(retry);
    };
  }, [meAttempt, signedOut]);

  const sites = me?.sites ?? [];
  // One site to look at is chosen already.
  const siteId = chosen ?? (sites.length === 1 ? sites[0]!.id : null);
  const site = sites.find((x) => x.id === siteId);
  const mustChange = me?.mustChangePassword ?? false;

  // The chosen site's board, always asked for by site: the 400 that brought us here cannot come back.
  useEffect(() => {
    if (!siteId || mustChange) return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    setBoard({ kind: 'loading' });
    void (async () => {
      const r = await fetchSnapshot(siteId);
      if (cancelled) return;
      if (r.ok) setBoard({ kind: 'board', snapshot: r.snapshot, at: Date.now() });
      else if (r.status === 401) signedOut();
      else if (r.status === null || r.status >= 500) retry = setTimeout(() => setBoardAttempt((n) => n + 1), RETRY_MS);
      else setBoard({ kind: 'refused', message: r.message ?? `The server would not show this site’s Wall (${r.status}).` });
    })();
    return () => {
      cancelled = true;
      clearTimeout(retry);
    };
  }, [siteId, mustChange, boardAttempt, signedOut]);

  const showing = board.kind === 'board';

  const rail = (
    <>
      <b>PREVIEW</b> · {site ? site.name.toUpperCase() : 'OFFICE'}
    </>
  );
  let stage;
  if (!me) stage = <BootScreen rail={rail}>Connecting…</BootScreen>;
  else if (mustChange) stage = <BootScreen rail={rail}>Choose your own password first.</BootScreen>;
  else if (sites.length === 0) stage = <BootScreen rail={rail}>You have no sites to preview.</BootScreen>;
  else if (!siteId) stage = <BootScreen rail={rail}>Choose a site above to preview its Wall.</BootScreen>;
  else if (board.kind === 'loading') stage = <BootScreen rail={rail}>Connecting…</BootScreen>;
  else if (board.kind === 'refused') stage = <BootScreen rail={rail}>{board.message}</BootScreen>;
  else
    stage = (
      <LiveWall
        key={board.at}
        initial={board.snapshot}
        siteId={siteId}
        onUnauthorized={signedOut}
        reload={reload}
        EventSourceImpl={EventSourceImpl}
      />
    );

  return (
    <div className="wall-app w-pv-app">
      <div className="w-pv" role="region" aria-label="Wall preview">
        {mustChange ? (
          <p className="w-pv-note">
            Choose your own password first. The one you signed in with was only temporary. <a href="/office">Open the office</a>
          </p>
        ) : (
          <>
            <div className="w-pv-pick" role="group" aria-labelledby={labelId}>
              <span id={labelId}>Preview the Wall for:</span>
              {sites.map((x) => (
                <button key={x.id} type="button" className="mono" aria-pressed={x.id === siteId} title={x.name} onClick={() => setChosen(x.id)}>
                  {x.shortCode}
                </button>
              ))}
            </div>
            {showing && site && (
              <p className="w-pv-note">
                <span className="w-pv-same">Preview: this is what the TV at {site.name} shows.</span>{' '}
                {me && can(me.role, 'settings') ? (
                  <>
                    To put it on a TV, pair the TV in <a href="/office/settings/devices">Settings → Devices</a>.
                  </>
                ) : (
                  'To put it on a TV, an admin pairs it in Settings → Devices.'
                )}
              </p>
            )}
          </>
        )}
      </div>
      <div className="w-pv-frame" ref={frame}>
        <div className="w-scale" style={{ transform: `scale(${fit.scale})`, left: fit.left, top: fit.top }}>
          {stage}
        </div>
      </div>
    </div>
  );
}
